import { makeAutoObservable } from 'mobx';
import { CurrencyService } from 'core/services/api/currency';
import { SiteConfig } from 'core/services/umbraco/config';
import { CurrencyData, CurrencyHelper } from 'utils/currency.util';
import { _noop } from 'utils/function.utils';

export class CurrencyStore {
  isLoading = false;
  mainCurrency!: CurrencyData;
  alternateCurrency!: CurrencyData;

  selectedAlt = false;
  exchangeRate!: number;
  onChangeCallback!: (currencyCode: string) => void;

  get inactiveCurrency() {
    return !this.selectedAlt ? this.alternateCurrency : this.mainCurrency;
  }

  get currency() {
    return this.selectedAlt ? this.alternateCurrency : this.mainCurrency;
  }

  get currencySymbol() {
    return this.currency.symbol;
  }

  get currencyCode() {
    return this.currency.code;
  }

  get conversionRate() {
    return this.selectedAlt ? this.exchangeRate : 1;
  }

  constructor() {
    makeAutoObservable(this)
  }

  initialise(country: string, config: SiteConfig, canExchange?: boolean, onChange: (currencyCode: string) => void = _noop) {
    this.changeCurrency({
      symbol: config.Currency,
      code: config.CurrencyCode
    });
    this.onChangeCallback = onChange;

    this.changeIsLoading(false);
    if (config.AlternateCurrency && canExchange) {
      this.changeIsLoading(true);

      this.changeAltCurrency({
        symbol: config.AlternateCurrency,
        code: config.AlternateCurrencyCode
      });

      if (config.SwitchCurrency) {
        this.changeSelectedCurrency();
      }

      CurrencyService.getExchangeRate(country, config.AlternateCurrencyCode)
        .then(
          res => {
            this.changeExchangeRate(res.conversionFactor)
            this.changeIsLoading(false);
          },
          error => {
            console.error(`Failed to fetch exchange rate for ${config.CurrencyCode}->${config.AlternateCurrencyCode}`, error);
            this.changeIsLoading(false);
          }
        )
    }
    return this;
  }

  getCurrencyValue(value?: number | string | null, pm?: boolean, format?: string) {
    if (value === null || value === undefined || isNaN(parseFloat(`${value}`))) {
      console.error(`unable to convert ${value} to a number`);
      return '';
    }
    return CurrencyHelper.FormatAmountToCurrency({ value: this.getExchangedValue(value), currencySymbol: this.currency?.symbol }, pm, format) || ''
  }

  getExchangedValue(value?: number | string | null) {
    if (value === null || value === undefined || isNaN(parseFloat(`${value}`))) {
      console.error(`unable to convert ${value} to a number`);
      return '';
    }
    try {
      return Math.round(((parseFloat(`${value}`.replace(',', '')) / this.conversionRate) + Number.EPSILON) * 100) / 100;
    }
    catch (error) {
      console.error(`unable to convert ${value} to a number`);
      return '';
    }
  }

  getAsMainCurrency(value?: number | string | null) {
    if (!value) {
      return null
    }

    if (!this.selectedAlt) {
      return +value
    }

    try {
      return Math.round(((parseFloat(`${value}`.replace(',', '')) * this.conversionRate) + Number.EPSILON) * 100) / 100;
    }
    catch (error) {
      console.error(`unable to convert ${value} to a number`);
      return '';
    }
  }

  changeIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  changeExchangeRate(exchangeRate: number) {
    this.exchangeRate = exchangeRate;
  }

  changeCurrency(currency: CurrencyData) {
    this.mainCurrency = currency;
  }

  changeSelectedCurrency(selected?: boolean) {
    this.selectedAlt = !this.selectedAlt;
    this.onChangeCallback(this.currency.code)
  }

  changeAltCurrency(currency: CurrencyData) {
    this.alternateCurrency = currency;
  }
}